import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../api/client';
import ArcaPageShell from './components/ArcaPageShell';

const emptyForm = (unidade) => ({
  nome: '',
  escopo: 'UNIDADE',
  unidade: unidade || '',
  logoUrl: '',
  corPrimaria: '#0f172a',
  corSecundaria: '#38bdf8',
  cabecalho: '',
  rodape: '',
  margemTopo: 100,
  margemBase: 80,
  margemLateral: 50
});

export default function ArcaFormulariosConfigPage() {
  const { unidadeSelecionada, isGlobalUser } = useAuth();
  const isGlobal = typeof isGlobalUser === 'function' ? isGlobalUser() : false;
  const [molduras, setMolduras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm(unidadeSelecionada?.codigo));
  const [templatePdf, setTemplatePdf] = useState(null);
  const [templateDocx, setTemplateDocx] = useState(null);
  const pdfInputRef = useRef(null);
  const docxInputRef = useRef(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const u = unidadeSelecionada?.codigo ? `?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}` : '';
      const res = await apiFetch(`/api/arca/formularios/molduras${u}`);
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data.error || 'Erro ao carregar molduras');
      setMolduras(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada?.codigo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirNova = () => {
    setEditingId(null);
    setForm(emptyForm(unidadeSelecionada?.codigo));
    setTemplatePdf(null);
    setTemplateDocx(null);
    setOpen(true);
  };

  const abrirEdicao = (m) => {
    setEditingId(m.id);
    setForm({
      nome: m.nome || '',
      escopo: m.escopo || 'UNIDADE',
      unidade: m.unidade || unidadeSelecionada?.codigo || '',
      logoUrl: m.logo_url || '',
      corPrimaria: m.cor_primaria || '#0f172a',
      corSecundaria: m.cor_secundaria || '#38bdf8',
      cabecalho: m.cabecalho || '',
      rodape: m.rodape || '',
      margemTopo: m.margem_topo ?? 100,
      margemBase: m.margem_base ?? 80,
      margemLateral: m.margem_lateral ?? 50
    });
    setTemplatePdf(null);
    setTemplateDocx(null);
    setOpen(true);
  };

  const salvar = async () => {
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('nome', form.nome);
      fd.append('escopo', form.escopo);
      if (form.escopo === 'UNIDADE') {
        fd.append('unidade', form.unidade || unidadeSelecionada?.codigo || '');
      }
      fd.append('logoUrl', form.logoUrl || '');
      fd.append('corPrimaria', form.corPrimaria);
      fd.append('corSecundaria', form.corSecundaria);
      fd.append('cabecalho', form.cabecalho || '');
      fd.append('rodape', form.rodape || '');
      fd.append('margemTopo', String(form.margemTopo));
      fd.append('margemBase', String(form.margemBase));
      fd.append('margemLateral', String(form.margemLateral));
      if (templatePdf) fd.append('templatePdf', templatePdf);
      if (templateDocx) fd.append('templateDocx', templateDocx);

      const url = editingId
        ? `/api/arca/formularios/molduras/${editingId}`
        : '/api/arca/formularios/molduras';
      const res = await apiFetch(url, {
        method: editingId ? 'PUT' : 'POST',
        body: fd
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      setOpen(false);
      setSnack(editingId ? 'Moldura atualizada' : 'Moldura salva');
      carregar();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remover = async (id) => {
    if (!window.confirm('Remover esta moldura?')) return;
    const res = await apiFetch(`/api/arca/formularios/molduras/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.error || 'Erro ao remover');
      return;
    }
    setSnack('Moldura removida');
    carregar();
  };

  return (
    <ArcaPageShell
      title="Configuração de Formulários"
      subtitle="Molduras (identidade visual) por unidade ou corporativo. Envie timbrado PDF e Word para exportação."
      maxWidth={900}
    >
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={carregar}>
          Atualizar
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNova}>
          Nova moldura
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {snack && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSnack('')}>
          {snack}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : molduras.length === 0 ? (
        <Alert severity="info">Nenhuma moldura cadastrada. Crie uma para a unidade ou corporativo.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {molduras.map((m) => (
            <Paper
              key={m.id}
              variant="outlined"
              sx={{ p: 2, cursor: 'pointer' }}
              onClick={() => abrirEdicao(m)}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    bgcolor: m.cor_primaria || '#0f172a',
                    border: `3px solid ${m.cor_secundaria || '#38bdf8'}`,
                    flexShrink: 0
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700}>{m.nome}</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={m.escopo === 'CORPORATIVO' ? 'Corporativo' : m.unidade || 'Unidade'}
                    />
                    {m.logo_url && <Chip size="small" variant="outlined" label="Com logo" />}
                    {m.template_pdf_path && (
                      <Chip size="small" color="error" variant="outlined" icon={<PictureAsPdfIcon />} label="PDF" />
                    )}
                    {m.template_docx_path && (
                      <Chip size="small" color="primary" variant="outlined" icon={<DescriptionIcon />} label="Word" />
                    )}
                  </Stack>
                  {(m.margem_topo || m.margem_base) && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Área de conteúdo: topo {m.margem_topo ?? 100}pt · base {m.margem_base ?? 80}pt · lateral{' '}
                      {m.margem_lateral ?? 50}pt
                    </Typography>
                  )}
                </Box>
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    remover(m.id);
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Editar moldura' : 'Nova moldura'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              fullWidth
            />
            <TextField
              select
              label="Escopo"
              value={form.escopo}
              onChange={(e) => setForm((f) => ({ ...f, escopo: e.target.value }))}
              fullWidth
            >
              <MenuItem value="UNIDADE">Unidade</MenuItem>
              {isGlobal && <MenuItem value="CORPORATIVO">Corporativo</MenuItem>}
            </TextField>
            {form.escopo === 'UNIDADE' && (
              <TextField
                label="Código da unidade"
                value={form.unidade}
                onChange={(e) => setForm((f) => ({ ...f, unidade: e.target.value }))}
                fullWidth
              />
            )}

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">
              Timbrado (upload)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Envie um PDF para exportação PDF e um Word (.docx) para exportação Word. O conteúdo do formulário será
              inserido na área central, respeitando as margens abaixo (espaço para cabeçalho e rodapé do timbrado).
            </Typography>

            <input
              ref={pdfInputRef}
              type="file"
              accept=".pdf,application/pdf"
              hidden
              onChange={(e) => setTemplatePdf(e.target.files?.[0] || null)}
            />
            <input
              ref={docxInputRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              hidden
              onChange={(e) => setTemplateDocx(e.target.files?.[0] || null)}
            />

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => pdfInputRef.current?.click()}
                fullWidth
              >
                {templatePdf ? templatePdf.name : 'Template PDF'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() => docxInputRef.current?.click()}
                fullWidth
              >
                {templateDocx ? templateDocx.name : 'Template Word'}
              </Button>
            </Stack>

            <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
              Margens da área de conteúdo (pontos)
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Topo (cabeçalho)"
                type="number"
                value={form.margemTopo}
                onChange={(e) => setForm((f) => ({ ...f, margemTopo: Number(e.target.value) || 100 }))}
                fullWidth
                helperText="Espaço reservado no topo"
              />
              <TextField
                label="Base (rodapé)"
                type="number"
                value={form.margemBase}
                onChange={(e) => setForm((f) => ({ ...f, margemBase: Number(e.target.value) || 80 }))}
                fullWidth
                helperText="Espaço reservado embaixo"
              />
              <TextField
                label="Lateral"
                type="number"
                value={form.margemLateral}
                onChange={(e) => setForm((f) => ({ ...f, margemLateral: Number(e.target.value) || 50 }))}
                fullWidth
                helperText="Esquerda e direita"
              />
            </Stack>

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">
              Identidade manual (fallback sem timbrado)
            </Typography>

            <TextField
              label="URL do logo"
              value={form.logoUrl}
              onChange={(e) => setForm((f) => ({ ...f, logoUrl: e.target.value }))}
              fullWidth
              helperText="Usado quando não há template PDF"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Cor primária"
                type="color"
                value={form.corPrimaria}
                onChange={(e) => setForm((f) => ({ ...f, corPrimaria: e.target.value }))}
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Cor secundária"
                type="color"
                value={form.corSecundaria}
                onChange={(e) => setForm((f) => ({ ...f, corSecundaria: e.target.value }))}
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Texto do cabeçalho"
              value={form.cabecalho}
              onChange={(e) => setForm((f) => ({ ...f, cabecalho: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Texto do rodapé"
              value={form.rodape}
              onChange={(e) => setForm((f) => ({ ...f, rodape: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={salvar} disabled={!form.nome || saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </ArcaPageShell>
  );
}
